import {ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ValidationErrors, ValidatorFn, Validators} from '@angular/forms';
import {MatIcon} from '@angular/material/icon';
import {ConfirmedCredentialFormFieldComponent} from '@aviary/forms/fields/confirmed-credential-form-field/confirmed-credential-form-field.component';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {CommonValidators, MAXIMUM_PASSWORD_LENGTH, MINIMUM_PASSWORD_LENGTH} from '@aviary/store/common/common.validators';

interface PasswordRequirement {
  readonly label: string;
  readonly errorName: string;
}

@Component({
  selector: 'px-confirmed-password-form-field',
  templateUrl: './confirmed-password-form-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ConfirmedCredentialFormFieldComponent, MatIcon]
})
export class ConfirmedPasswordFormFieldComponent implements OnInit, OnDestroy {
  @Input({required: true}) public fieldName: string;
  @Input({required: true}) public formGroup: ExtendedFormGroup;
  @Input() public passwordLabel: string = 'Password';
  @Input() public confirmationLabel: string = 'Confirm Password';

  /*
    NOTE:  These requirements are currently stricter than Firebase's configuration, so we shouldn't be at risk of accepting a password that Firebase rejects.
    If Firebase gets stricter someday, we might need to use its validatePassword() function instead of our own validators.
  */
  public readonly maximumPasswordLength: number = MAXIMUM_PASSWORD_LENGTH;
  public readonly requirements: PasswordRequirement[] = [
    {label: `${MINIMUM_PASSWORD_LENGTH} characters minimum`, errorName: 'minlength'},
    {label: 'one uppercase character', errorName: 'requiresUppercase'},
    {label: 'one number', errorName: 'requiresDigit'},
    {label: 'one lowercase character', errorName: 'requiresLowercase'},
    {label: 'one symbol', errorName: 'requiresSpecialChars'}
  ];
  public passwordControl: ExtendedFormControl;
  public passwordRequirementErrors: ValidationErrors | null;

  private readonly changeDetector: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly requiredValidator: ValidatorFn = Validators.required;
  private readonly passwordRequirementsValidator: ValidatorFn = Validators.compose(CommonValidators.passwordValidators) as ValidatorFn;
  private readonly formGroupPasswordRequirementsValidator: ValidatorFn = (): ValidationErrors | null =>
    this.passwordRequirementsValidator(this.passwordControl) ? {passwordRequirements: true} : null;

  public ngOnInit(): void {
    this.passwordControl = getExtendedControlFrom(this.formGroup, this.fieldName);
    this.passwordControl.addValidators(this.requiredValidator);
    this.formGroup.addValidators(this.formGroupPasswordRequirementsValidator);
    this.passwordControl.updateValueAndValidity({emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
    this.updatePasswordRequirementErrors();

    this.passwordControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.updatePasswordRequirementErrors();
      this.changeDetector.markForCheck();
    });
  }

  public ngOnDestroy(): void {
    this.passwordControl.removeValidators(this.requiredValidator);
    this.formGroup.removeValidators(this.formGroupPasswordRequirementsValidator);
    this.passwordControl.updateValueAndValidity({emitEvent: false});
    this.formGroup.updateValueAndValidity({emitEvent: false});
  }

  public isRequirementSatisfied(requirement: PasswordRequirement): boolean {
    return !!this.passwordControl.value && !this.passwordRequirementErrors?.[requirement.errorName];
  }

  private updatePasswordRequirementErrors(): void {
    this.passwordRequirementErrors = this.passwordRequirementsValidator(this.passwordControl);
  }
}
